class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    if (facetForm) {
      facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);

    // Set up sort select synchronization
    FacetFiltersForm.setupSortSelectSync();
  }

  static setupSortSelectSync() {
    // Set up change listeners for all sort selects
    function setupSortSelects() {
      // Desktop select
      const sortBySelect = document.getElementById("SortBy");
      if (sortBySelect && !sortBySelect.dataset.sortSyncAttached) {
        sortBySelect.dataset.sortSyncAttached = "true";
        sortBySelect.addEventListener("change", function () {
          FacetFiltersForm.syncSortSelects(this.value);
        });
      }

      // Mobile selects
      const sortByMobileSelects = document.querySelectorAll(
        'select[id^="SortBy-mobile-"]'
      );
      sortByMobileSelects.forEach((select) => {
        if (!select.dataset.sortSyncAttached) {
          select.dataset.sortSyncAttached = "true";
          select.addEventListener("change", function () {
            FacetFiltersForm.syncSortSelects(this.value);
          });
        }
      });
    }

    // Initialize on load
    function init() {
      setupSortSelects();
      // Sync initial values
      const sortBySelect = document.getElementById("SortBy");
      const sortByMobileSelects = document.querySelectorAll(
        'select[id^="SortBy-mobile-"]'
      );

      if (sortBySelect && sortBySelect.value) {
        FacetFiltersForm.syncSortSelects(sortBySelect.value);
      } else if (
        sortByMobileSelects.length > 0 &&
        sortByMobileSelects[0].value
      ) {
        FacetFiltersForm.syncSortSelects(sortByMobileSelects[0].value);
      }
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
    } else {
      init();
    }

    // Re-initialize after page updates (debounced to avoid too many calls)
    let setupTimeout;
    const observer = new MutationObserver(function () {
      clearTimeout(setupTimeout);
      setupTimeout = setTimeout(setupSortSelects, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );

    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection-grid-container")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        // Sync sort selects after render
        const sortBySelect = document.getElementById("SortBy");
        if (sortBySelect) {
          const currentValue = sortBySelect.value;
          FacetFiltersForm.syncSortSelects(currentValue);
          sortBySelect.dispatchEvent(new Event("change", { bubbles: true }));
        } else {
          // If desktop select doesn't exist, try mobile
          const sortByMobileSelects = document.querySelectorAll(
            'select[id^="SortBy-mobile-"]'
          );
          if (sortByMobileSelects.length > 0) {
            const currentValue = sortByMobileSelects[0].value;
            FacetFiltersForm.syncSortSelects(currentValue);
          }
        }

        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    // Sync sort selects after render
    const sortBySelect = document.getElementById("SortBy");
    if (sortBySelect) {
      const currentValue = sortBySelect.value;
      FacetFiltersForm.syncSortSelects(currentValue);
      sortBySelect.dispatchEvent(new Event("change", { bubbles: true }));
    } else {
      // If desktop select doesn't exist, try mobile
      const sortByMobileSelects = document.querySelectorAll(
        'select[id^="SortBy-mobile-"]'
      );
      if (sortByMobileSelects.length > 0) {
        const currentValue = sortByMobileSelects[0].value;
        FacetFiltersForm.syncSortSelects(currentValue);
      }
    }

    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });

    // Re-initialize grid toggle after grid update
    if (typeof initGridToggle === "function") {
      initGridToggle();
    }

    // Informuj datalayer.js o odświeżeniu siatki (filtry / sort)
    document.dispatchEvent(new CustomEvent("collection-grid-updated", {
      detail: { source: "filter" }
    }));
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const productCountElement = parsedHTML.getElementById("ProductCount");

    if (!productCountElement) {
      return;
    }

    const count = productCountElement.innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");

    if (container) {
      container.innerHTML = count;
      container.classList.remove("loading");
    }

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        /*
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
        */
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter")
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementToActivate =
          newFacetDetailsElement.querySelector(".facets__summary");

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item")
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    // console.log(new URLSearchParams(formData).toString());
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const params = new URLSearchParams();

    const currentUrl = new URL(window.location.href);
    const sortParam = currentUrl.searchParams.get("sort_by");
    if (sortParam) {
      params.set("sort_by", sortParam);
    }

    // Get sort value from desktop or mobile select (prioritize the one that triggered the event)
    const sortBySelect = document.getElementById("SortBy");
    const sortByMobileSelects = document.querySelectorAll(
      'select[id^="SortBy-mobile-"]'
    );

    // If event was triggered by a select, use its value
    if (
      event.target &&
      event.target.tagName === "SELECT" &&
      event.target.name === "sort_by"
    ) {
      params.set("sort_by", event.target.value);
      // Sync to other selects
      FacetFiltersForm.syncSortSelects(event.target.value);
    } else {
      // Otherwise, check desktop first, then mobile
      if (sortBySelect && sortBySelect.value) {
        params.set("sort_by", sortBySelect.value);
        FacetFiltersForm.syncSortSelects(sortBySelect.value);
      } else if (
        sortByMobileSelects.length > 0 &&
        sortByMobileSelects[0].value
      ) {
        params.set("sort_by", sortByMobileSelects[0].value);
        FacetFiltersForm.syncSortSelects(sortByMobileSelects[0].value);
      }
    }

    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );

    sortFilterForms.forEach((form) => {
      const checkboxInputs = form.querySelectorAll('input[type="checkbox"]');
      checkboxInputs.forEach((input) => {
        if (input.checked) {
          params.append(input.name, input.value);
        }
      });

      const otherInputs = form.querySelectorAll('input:not([type="checkbox"])');
      otherInputs.forEach((input) => {
        if (input.value && input.name !== "sort_by") {
          params.set(input.name, input.value);
        }
      });

      const selectElements = form.querySelectorAll("select");
      selectElements.forEach((select) => {
        if (select.value && select.name === "sort_by") {
          params.set("sort_by", select.value);
        }
      });
    });

    this.onSubmitForm(params.toString(), event);
  }

  static syncSortSelects(value) {
    // Sync desktop select
    const sortBySelect = document.getElementById("SortBy");
    if (sortBySelect && sortBySelect.value !== value) {
      sortBySelect.value = value;

      // Update selected attribute on option
      const options = sortBySelect.querySelectorAll("option");
      options.forEach((option) => {
        if (option.value === value) {
          option.setAttribute("selected", "selected");
        } else {
          option.removeAttribute("selected");
        }
      });

      // Update custom-select component if it exists
      const customSelect = sortBySelect.closest("custom-select");
      if (customSelect) {
        const newSelectedOption = customSelect.querySelector(
          `.js-btn-option[data-value="${value}"]`
        );
        const currentSelectedOption = customSelect.querySelector(
          ".js-btn-option.is-selected"
        );

        if (newSelectedOption && currentSelectedOption !== newSelectedOption) {
          // Use the changeSelectedOption method if available
          if (typeof customSelect.changeSelectedOption === "function") {
            customSelect.changeSelectedOption(
              currentSelectedOption,
              newSelectedOption
            );
          } else {
            // Fallback: manually update the button and trigger input event
            customSelect.dataset.value = value;
            if (customSelect.btnToggleDropdown) {
              customSelect.btnToggleDropdown.innerHTML =
                newSelectedOption.textContent +
                customSelect.dataset.iconChevronDown;
            }
            currentSelectedOption.classList.remove("is-selected");
            currentSelectedOption.setAttribute("aria-selected", false);
            newSelectedOption.classList.add("is-selected");
            newSelectedOption.setAttribute("aria-selected", true);

            // Trigger input event to notify form handlers
            const inputEvent = new Event("input", { bubbles: true });
            sortBySelect.dispatchEvent(inputEvent);
          }
        }
      } else {
        // If no custom-select, just trigger change event
        const changeEvent = new Event("change", { bubbles: true });
        sortBySelect.dispatchEvent(changeEvent);
      }
    }

    // Sync all mobile selects
    const sortByMobileSelects = document.querySelectorAll(
      'select[id^="SortBy-mobile-"]'
    );
    sortByMobileSelects.forEach((select) => {
      if (select.value !== value) {
        select.value = value;

        // Update selected attribute on option
        const options = select.querySelectorAll("option");
        options.forEach((option) => {
          if (option.value === value) {
            option.setAttribute("selected", "selected");
          } else {
            option.removeAttribute("selected");
          }
        });

        // Update custom-select component if it exists
        const customSelect = select.closest("custom-select");
        if (customSelect) {
          const newSelectedOption = customSelect.querySelector(
            `.js-btn-option[data-value="${value}"]`
          );
          const currentSelectedOption = customSelect.querySelector(
            ".js-btn-option.is-selected"
          );

          if (
            newSelectedOption &&
            currentSelectedOption !== newSelectedOption
          ) {
            // Use the changeSelectedOption method if available
            if (typeof customSelect.changeSelectedOption === "function") {
              customSelect.changeSelectedOption(
                currentSelectedOption,
                newSelectedOption
              );
            } else {
              // Fallback: manually update the button and trigger input event
              customSelect.dataset.value = value;
              if (customSelect.btnToggleDropdown) {
                customSelect.btnToggleDropdown.innerHTML =
                  newSelectedOption.textContent +
                  customSelect.dataset.iconChevronDown;
              }
              currentSelectedOption.classList.remove("is-selected");
              currentSelectedOption.setAttribute("aria-selected", false);
              newSelectedOption.classList.add("is-selected");
              newSelectedOption.setAttribute("aria-selected", true);

              // Trigger input event to notify form handlers
              const inputEvent = new Event("input", { bubbles: true });
              select.dispatchEvent(inputEvent);
            }
          }
        } else {
          // If no custom-select, just trigger change event
          const changeEvent = new Event("change", { bubbles: true });
          select.dispatchEvent(changeEvent);
        }
      }
    });
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    const allowedKeys = [
      "Backspace",
      "Tab",
      "Enter",
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Escape",
    ];
    if (!allowedKeys.includes(event.key) && !/[0-9.,]/.test(event.key)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) {
      minInput.setAttribute("data-max", maxInput.value);
    } else {
      minInput.setAttribute("data-max", maxInput.getAttribute("max"));
    }

    if (minInput.value) {
      maxInput.setAttribute("data-min", minInput.value);
    } else {
      maxInput.setAttribute("data-min", minInput.getAttribute("min"));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min")) || 0; // Default to input's min attribute
    const max = Number(input.getAttribute("max")); // Default to input's max attribute

    if (!isNaN(min) && value < min) input.value = min;
    if (!isNaN(max) && value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

// Grid toggle functionality for mobile
function initGridToggle() {
  const gridToggleContainers = document.querySelectorAll(
    ".collection-facets__grid-toggle"
  );

  gridToggleContainers.forEach((container) => {
    // Skip if already initialized
    if (container.dataset.gridToggleInitialized) return;
    container.dataset.gridToggleInitialized = "true";

    const gridToggleButtons = container.querySelectorAll(
      ".collection-facets__grid-toggle-btn"
    );
    const productGrid = document.getElementById("product-grid");

    if (!productGrid || gridToggleButtons.length === 0) return;

    // Initialize state: ensure only button with 1 column is active and grid has single class
    const button1Col = Array.from(gridToggleButtons).find(
      (btn) => btn.dataset.gridCols === "1"
    );
    const button2Col = Array.from(gridToggleButtons).find(
      (btn) => btn.dataset.gridCols === "2"
    );

    // Remove active from all buttons first
    gridToggleButtons.forEach((btn) => btn.classList.remove("active"));

    // Set initial state: 1 column is default
    if (button1Col) {
      button1Col.classList.add("active");
      productGrid.classList.add("collection__grid-single");
    } else if (button2Col) {
      // Fallback if 1 column button doesn't exist
      button2Col.classList.add("active");
      productGrid.classList.remove("collection__grid-single");
    }

    // Store reference to new buttons after cloning
    const newButtons = [];

    gridToggleButtons.forEach((button) => {
      // Remove existing listeners if any
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      newButtons.push(newButton);

      newButton.addEventListener("click", (e) => {
        e.preventDefault();
        const cols = parseInt(newButton.dataset.gridCols, 10);

        // Update active state - use newButtons array
        newButtons.forEach((btn) => btn.classList.remove("active"));
        newButton.classList.add("active");

        // Toggle grid class
        if (cols === 1) {
          productGrid.classList.add("collection__grid-single");
        } else {
          productGrid.classList.remove("collection__grid-single");
        }
      });
    });

    // Re-apply initial state after cloning to ensure correct state
    newButtons.forEach((btn) => btn.classList.remove("active"));
    const newButton1Col = newButtons.find(
      (btn) => btn.dataset.gridCols === "1"
    );
    if (newButton1Col) {
      newButton1Col.classList.add("active");
      productGrid.classList.add("collection__grid-single");
    }
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initGridToggle);
} else {
  initGridToggle();
}

// Re-initialize after page updates (for AJAX updates)
let gridToggleTimeout;
const gridToggleObserver = new MutationObserver(() => {
  clearTimeout(gridToggleTimeout);
  gridToggleTimeout = setTimeout(initGridToggle, 100);
});

gridToggleObserver.observe(document.body, {
  childList: true,
  subtree: true,
});
